import { useMemo, useState } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQuery } from '@tanstack/react-query';
import {
  projectAssignableRolesQuery,
  projectMembersQuery,
  projectRoleAssignmentsQuery,
  workspaceUsersQuery,
} from '@/entities/project-member';
import { useProjectCapabilities } from '@/entities/user';
import { useUpdateProjectMembers } from '@/features/manage-project-members/api/useUpdateProjectMembers.mutation';
import { notificationService } from '@/lib/services/notification.service';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import type { MembershipSnapshot } from '../../lib/approval-step-shortfalls';
import {
  checkableWithRoleAssignments,
  useProjectApprovalChains,
  type RoleAssignmentsRead,
} from '../../lib/useProjectApprovalChains';
import { ApprovalChainImpactNotice } from '../ApprovalChainImpactNotice';
import { ManageMembersModal } from '../ManageMembersModal';
import type { ProjectMembersModalProps } from './ProjectMembersModal.types';
import { useTranslation } from 'react-i18next';

/**
 * Project-members surface. A thin wrapper over the shared
 * `ManageMembersModal`: it injects the project data source (members +
 * workspace users queries) and the platform-API update mutation. All modal
 * UX (staging, add/remove, layout) lives in the shared shell.
 */
export function ProjectMembersModal(props: ProjectMembersModalProps) {
  const { t } = useTranslation('projects');

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const projectRolesEnabled = useBooleanFlagValue('project_roles', false);
  const projects = useProjectCapabilities();
  const canManageMembers = projects.canManageMembers;

  const membersQuery = useQuery(projectMembersQuery(props.workspaceId, props.platformProjectId));

  // Everything below the member list is for changing it, and the platform
  // refuses all of it without `platform:manage-project-members`: the add
  // picker's roster, the role catalogue, and the per-member assignments. Asking
  // anyway is how a reader who can only look ends up with a screenful of 403s.
  // Conjoin rather than replace: each helper carries its own reason to stay
  // idle (no workspace, a search term too short to send, no project id), and
  // overwriting `enabled` would fire the very requests it declines.
  const workspaceUsersOptions = workspaceUsersQuery(props.workspaceId, debouncedSearch);
  const workspaceQuery = useQuery({
    ...workspaceUsersOptions,
    enabled: workspaceUsersOptions.enabled && canManageMembers,
  });

  const rolesOptions = projectAssignableRolesQuery(props.platformProjectId);
  const rolesQuery = useQuery({
    ...rolesOptions,
    enabled: rolesOptions.enabled && projectRolesEnabled && canManageMembers,
  });

  const assignmentsOptions = projectRoleAssignmentsQuery(props.platformProjectId);
  const assignmentsQuery = useQuery({
    ...assignmentsOptions,
    enabled: assignmentsOptions.enabled && projectRolesEnabled && canManageMembers,
  });

  const updateMembers = useUpdateProjectMembers({
    workspaceId: props.workspaceId,
    platformProjectId: props.platformProjectId,
  });

  const serverMembers = useMemo(() => membersQuery.data?.data ?? [], [membersQuery.data]);
  const membershipBefore = useMemo<MembershipSnapshot>(() => {
    const assignments = assignmentsQuery.data ?? [];
    return {
      memberIds: new Set(serverMembers.map((member) => member.id)),
      holdsRole: (userId, roleId) =>
        assignments.some((a) => a.identityId === userId && a.roleId === roleId),
    };
  }, [serverMembers, assignmentsQuery.data]);
  const approvalChains = checkableWithRoleAssignments(
    useProjectApprovalChains(props.platformProjectId, props.open && canManageMembers),
    roleAssignmentsRead(assignmentsQuery),
  );
  const workspaceUsers = workspaceQuery.data?.data ?? [];

  const handleSave = (memberIds: string[], roles?: Array<{ userId: string; roleId: string }>) => {
    updateMembers.mutate(
      { userIds: memberIds, roles },
      {
        onSuccess: () => {
          notificationService.showSuccess(
            t('manageProjectMembers.projectMembersModal.projectMembersUpdated'),
            {
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            },
          );
          // `onClose` alone — the host unmounts on its own timer.
          props.onClose();
        },
      },
    );
  };

  return (
    <ManageMembersModal
      open={props.open}
      onClose={props.onClose}
      // Trackers shares this shell and keeps the centred dialog, which is why
      // the surface is chosen per caller rather than inside it.
      surface="panel"
      title={t('manageProjectMembers.projectMembersModal.projectMembers')}
      ownerId={props.ownerId}
      members={serverMembers}
      workspaceUsers={workspaceUsers}
      isLoadingUsers={workspaceQuery.isLoading}
      isLoadingMembers={membersQuery.isLoading}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
      onSave={handleSave}
      isSaving={updateMembers.isPending}
      canManage={canManageMembers}
      roles={{
        enabled: projectRolesEnabled && canManageMembers,
        canEdit: canManageMembers,
        availableRoles: rolesQuery.data ?? [],
        assignments: assignmentsQuery.data ?? [],
        isLoading: rolesQuery.isLoading || assignmentsQuery.isLoading,
      }}
      renderChangeNotice={(staged) => (
        <ApprovalChainImpactNotice
          chains={approvalChains}
          before={membershipBefore}
          after={staged}
        />
      )}
    />
  );
}

/** A query the flag or the caller's rights keep idle reads as unavailable, the same as one that failed. */
function roleAssignmentsRead(query: { data?: unknown; isLoading: boolean }): RoleAssignmentsRead {
  if (query.data) return 'ready';
  return query.isLoading ? 'loading' : 'unavailable';
}
